import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Banner, Button, DataTable, Labeled, Segmented, SelectField, Skel, ToggleChip, useElementWidth, useIsMobile,
} from '../ui';
import { MetricChart, binAverage } from '../../charts';
import {
    CHARTABLE_METRICS, METRICS, binFor, defaultJoint, eventsOn, fmtClock, fmtParamValue, humanize, metricLabel,
    samplesFor, seriesCatalog, skeletonJoints, toGrid, tolFor, toMs,
} from '../../seriesExtractors';
import ReplayPanel from './ReplayPanel';
import SeriesPanel from './SeriesPanel';
import EventTimeline, { EventTypeChips } from './EventTimeline';
import { eventTypesOf, withTypeColors } from './eventTypes';
import { downloadCSV, downloadJSON } from './metricExport';
import { useTheme } from '../../ThemeContext';

const VIEWS = [{ value: 'combined', label: 'Combined' }, { value: 'individual', label: 'Individual' }];
const SCALES = [{ value: 'full', label: 'Full' }, { value: '1s', label: '1s' }, { value: '10s', label: '10s' }];
const CHIP_DEFAULT = { skeleton: true, robotic: true, biometric: true, hands: true, parameter: false, sessionevent: true };

const listJoin = (a) => (a.length <= 1 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);

/** Default series: the first moving group's natural set, plus GSR on its own right axis. */
function defaultSelection(cats) {
    const has = (k) => cats[k] && cats[k].length;
    const sel = [];
    if (has('skeleton')) sel.push('sk:pos:x', 'sk:pos:y', 'sk:pos:z');
    else if (has('hands')) sel.push('h:right:x', 'h:right:y', 'h:right:z');
    else if (has('robotic')) cats.robotic.forEach((d) => sel.push(d.key));
    if (has('biometric')) {
        if (!sel.length) cats.biometric.forEach((d) => sel.push(d.key));
        else if (cats.biometric.some((d) => d.key === 'b:GSR')) sel.push('b:GSR');
    }
    return new Set(sel);
}

/**
 * The metric-data workspace: toolbar (metric chips, view, timescale, export), partial-failure
 * banner, sticky replay, combined chart + series panel or per-metric charts with the
 * parameter / event tables. Owns the replay clock so only this subtree re-renders while playing.
 */
export default function MetricWorkspace({ id, metrics, okData, timeline, retry }) {
    const isMobile = useIsMobile();
    const [wref, contentW] = useElementWidth(1200);

    const [chips, setChips] = useState(() => {
        const c = {};
        METRICS.forEach(({ key }) => { c[key] = metrics[key]?.status === 'empty' ? false : CHIP_DEFAULT[key]; });
        return c;
    });
    const [view, setView] = useState('combined');
    const [timescale, setTimescale] = useState('full');
    const [sel, setSel] = useState(null);
    const [jointSel, setJoint] = useState(null);
    const [seriesOpen, setSeriesOpen] = useState(null);
    const [skelMode, setSkelMode] = useState('pose');
    const [retrying, setRetrying] = useState(false);

    const st = (k) => metrics[k]?.status;
    const failed = METRICS.filter((m) => st(m.key) === 'failed').map((m) => m.key);
    const groupOn = (k) => chips[k] && st(k) === 'ok';

    /* ── time base, joints, catalogs ─────────────────────────────────── */
    const { t0, hz, duration } = timeline;
    const joints = useMemo(() => skeletonJoints(okData.skeleton), [okData.skeleton]);
    const joint = jointSel && joints.includes(jointSel) ? jointSel : defaultJoint(joints);
    const cats = useMemo(() => {
        const c = {};
        CHARTABLE_METRICS.forEach((k) => { c[k] = seriesCatalog(k, okData[k], joint); });
        return c;
    }, [okData, joint]);
    const defaultSel = useMemo(() => defaultSelection(cats), [cats]);
    const selected = sel || defaultSel;

    // Raw grids per series key + joint, rebuilt only when data or the time base changes.
    const cache = useMemo(() => new Map(), [okData, timeline]); // eslint-disable-line react-hooks/exhaustive-deps
    const gridOf = useCallback((def) => {
        const ck = def.key + '|' + (def.group === 'skeleton' ? joint : '');
        if (!cache.has(ck)) cache.set(ck, toGrid(samplesFor(def, okData[def.group], joint, t0), timeline, tolFor(def.group, timeline)));
        return cache.get(ck);
    }, [cache, joint, okData, t0, timeline]);
    const bin = binFor(timescale, hz);
    const withData = useCallback((defs) => defs.map((d, i) => ({ ...d, idx: i, data: binAverage(gridOf(d), bin) })), [gridOf, bin]);

    const visibleDefs = useMemo(
        () => CHARTABLE_METRICS.filter(groupOn).flatMap((k) => cats[k].filter((d) => selected.has(d.key))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cats, selected, chips, metrics],
    );
    const combined = useMemo(() => (view === 'combined' ? withData(visibleDefs) : []), [view, withData, visibleDefs]);
    const leftUnits = [...new Set(combined.filter((s) => s.axis !== 'right' && s.unit).map((s) => s.unit))];
    const leftUnit = leftUnits.length === 1 && combined.filter((s) => s.axis !== 'right').every((s) => s.unit) ? leftUnits[0] : '';
    const rightUnit = combined.some((s) => s.axis === 'right') ? combined.filter((s) => s.axis === 'right').map((s) => s.label).join(' · ') : '';

    // Events in their game's types and colours; a type turned off in the legend is hidden everywhere.
    const { theme } = useTheme();
    const rawEvents = useMemo(() => eventsOn(okData.sessionevent, t0), [okData.sessionevent, t0]);
    const eventTypes = useMemo(() => eventTypesOf(rawEvents, theme), [rawEvents, theme]);
    const events = useMemo(() => withTypeColors(rawEvents, eventTypes), [rawEvents, eventTypes]);
    const [hiddenTypes, setHiddenTypes] = useState(() => new Set());
    // a chip's keys: one type's, or all of the legend's "Other"; all off turns them on, else off
    const toggleType = useCallback((keys) => setHiddenTypes((h) => {
        const next = new Set(h);
        const allHidden = keys.every((k) => next.has(k));
        keys.forEach((k) => { if (allHidden) next.delete(k); else next.add(k); });
        return next;
    }), []);
    const shownEvents = useMemo(() => events.filter((e) => !hiddenTypes.has(e.typeKey)), [events, hiddenTypes]);
    // the legend only when the game gave types (an older session's events are all plain "Event")
    const typed = eventTypes.some((ty) => ty.key);
    const markers = chips.sessionevent && st('sessionevent') === 'ok' ? shownEvents : [];
    const lastEventNote = st('sessionevent') === 'failed' ? 'Events didn’t load'
        : !chips.sessionevent ? 'Events hidden'
            : events.length ? 'None yet' : 'No events recorded';

    /* ── replay clock ────────────────────────────────────────────────── */
    const [t, setT] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const tRef = useRef(0);
    const speedRef = useRef(1);
    useEffect(() => { speedRef.current = speed; }, [speed]);
    const seek = useCallback((x) => {
        const v = Math.max(0, Math.min(duration, Number(x) || 0));
        tRef.current = v;
        setT(v);
    }, [duration]);
    useEffect(() => { if (tRef.current > duration) seek(duration); }, [duration, seek]);
    useEffect(() => {
        if (!playing) return undefined;
        if (tRef.current >= duration - 0.05) seek(0);
        // ~20 updates/s is plenty for 3–30 Hz recordings and keeps the chart re-render cheap.
        let last = performance.now();
        const timer = setInterval(() => {
            const now = performance.now();
            const nt = Math.min(duration, tRef.current + ((now - last) / 1000) * speedRef.current);
            last = now;
            tRef.current = nt;
            setT(nt);
            if (nt >= duration) setPlaying(false);
        }, 50);
        return () => clearInterval(timer);
    }, [playing, duration, seek]);
    const nudge = useCallback((d) => seek(tRef.current + d), [seek]);
    const togglePlay = useCallback(() => setPlaying((p) => !p), []);

    /* ── actions ─────────────────────────────────────────────────────── */
    const toggleSel = (key) => setSel(() => {
        const next = new Set(selected);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const retryFailed = () => {
        if (!failed.length || retrying) return;
        setRetrying(true);
        retry(failed).finally(() => setRetrying(false));
    };
    const exportable = {};
    METRICS.forEach(({ key }) => { if (groupOn(key) && okData[key]) exportable[key] = okData[key]; });
    const canExport = Object.keys(exportable).length > 0;

    /* ── layout ──────────────────────────────────────────────────────── */
    const narrow = isMobile || contentW < 880;
    const open = seriesOpen ?? !isMobile;
    const isCombined = view === 'combined';
    const seriesSide = isCombined && open && !narrow && contentW >= 1560;
    const seriesBelow = isCombined && open && !seriesSide;
    const cols = narrow ? 'minmax(0,1fr)' : seriesSide ? 'minmax(380px,.85fr) minmax(0,1.5fr) 300px' : 'minmax(360px,.8fr) minmax(0,1.4fr)';

    const groups = CHARTABLE_METRICS.filter((k) => chips[k]).map((k) => ({
        key: k,
        label: metricLabel(k),
        failed: st(k) === 'failed',
        empty: st(k) === 'empty',
        loading: st(k) === 'loading',
        items: st(k) === 'ok' ? cats[k].map((d) => ({ key: d.key, item: d.item, checked: selected.has(d.key), idx: visibleDefs.indexOf(d) })) : [],
    }));
    const series = (layout) => (
        <SeriesPanel
            groups={groups}
            joint={joint}
            joints={joints}
            onJoint={setJoint}
            onToggle={toggleSel}
            shownCount={visibleDefs.length}
            onClose={() => setSeriesOpen(false)}
            layout={layout}
        />
    );

    /* ── chips + partial banner copy ─────────────────────────────────── */
    const shownLabels = METRICS.filter((m) => st(m.key) === 'ok').map((m) => m.label);
    const failedLabels = failed.map(metricLabel);

    const chartableOn = CHARTABLE_METRICS.filter((k) => chips[k]);
    const indivCards = useMemo(() => {
        if (view !== 'individual') return [];
        return CHARTABLE_METRICS.filter((k) => chips[k]).map((k) => {
            const s = metrics[k]?.status;
            let defs = s === 'ok' ? cats[k] : [];
            if (k === 'skeleton') defs = defs.filter((d) => d.key.startsWith('sk:pos'));
            return { k, s, defs, series: withData(defs) };
        });
    }, [view, chips, metrics, cats, withData]);

    const paramRows = useMemo(() => {
        const posts = okData.parameter || [];
        const rows = [];
        posts.forEach((g, gi) => {
            const ts = toMs(g?.timestamp);
            (Array.isArray(g?.data) ? g.data : []).forEach((p, pi) => {
                rows.push({ id: `${gi}-${pi}`, at: ts != null ? fmtClock((ts - t0) / 1000) : '—', grp: g?.name || '—', n: p?.name || '—', v: fmtParamValue(p?.value_), ty: p?.parm_type || '—' });
            });
        });
        return rows;
    }, [okData.parameter, t0]);
    const multiPost = (okData.parameter || []).length > 1;

    return (
        <div ref={wref} className="sd-metrics-body">
            <div role="toolbar" aria-label="Metric controls" className="sd-toolbar">
                <div role="group" aria-label="Metric groups" className="eg-chip-group">
                    {METRICS.map((m) => (
                        <ToggleChip
                            key={m.key}
                            on={chips[m.key]}
                            failed={st(m.key) === 'failed'}
                            title={st(m.key) === 'failed' ? `${m.label} failed to load` : st(m.key) === 'empty' ? `${m.label}: nothing recorded in this session` : undefined}
                            onToggle={() => setChips((c) => ({ ...c, [m.key]: !c[m.key] }))}
                        >
                            {m.label}
                        </ToggleChip>
                    ))}
                </div>
                <div className="sd-toolbar__segs">
                    <Labeled label="View"><Segmented options={VIEWS} value={view} onChange={setView} label="View" /></Labeled>
                    <Labeled label="Timescale"><Segmented options={SCALES} value={timescale} onChange={setTimescale} label="Timescale" /></Labeled>
                </div>
                <span className="eg-spacer" />
                <div className="sd-toolbar__export">
                    <Button icon="download" disabled={!canExport} onClick={() => downloadJSON(id, exportable)} title="Download the selected metrics as JSON">JSON</Button>
                    <Button icon="download" disabled={!canExport} onClick={() => downloadCSV(id, exportable)} title="Download the selected metrics as CSV">CSV</Button>
                </div>
            </div>

            {failed.length ? (
                <Banner
                    tone="danger"
                    title={`${listJoin(failedLabels)} didn’t load.`}
                    action={<Button onClick={retryFailed} disabled={retrying}>{retrying ? 'Retrying…' : 'Retry failed'}</Button>}
                >
                    {shownLabels.length ? `${listJoin(shownLabels)} ${shownLabels.length === 1 ? 'is' : 'are'} shown.` : null}
                </Banner>
            ) : null}

            <div className="sd-ws" style={{ gridTemplateColumns: cols }}>
                <ReplayPanel
                    frames={okData.skeleton}
                    skelStatus={st('skeleton')}
                    t0={t0}
                    duration={duration}
                    t={t}
                    playing={playing}
                    speed={speed}
                    mode={skelMode}
                    onMode={setSkelMode}
                    onSeek={seek}
                    onNudge={nudge}
                    onTogglePlay={togglePlay}
                    onSpeed={setSpeed}
                    events={markers}
                    lastEventNote={lastEventNote}
                    sticky={!narrow}
                    height={isMobile ? 280 : 400}
                />

                <div className="sd-main">
                    {isCombined ? (
                        <>
                            <div className="eg-box-card">
                                <div className="sd-chart-head">
                                    {typed && chips.sessionevent && st('sessionevent') === 'ok' ? (
                                        <EventTypeChips types={eventTypes} hidden={hiddenTypes} onToggle={toggleType} theme={theme} />
                                    ) : null}
                                    <span style={{ flex: 1 }} />
                                    {!open ? <Button size="sm" className="sd-series-btn" onClick={() => setSeriesOpen(true)}>Series · {visibleDefs.length} shown</Button> : null}
                                </div>
                                <MetricChart
                                    series={combined}
                                    duration={duration}
                                    height={isMobile ? 300 : 440}
                                    markers={markers}
                                    leftUnit={leftUnit}
                                    rightUnit={rightUnit}
                                    playhead={t}
                                    onSeek={seek}
                                    emptyText={chartableOn.some((k) => st(k) === 'ok') ? 'No series selected' : 'No metric turned on'}
                                />
                            </div>
                            {seriesBelow ? series(isMobile ? 'stack' : 'below') : null}
                        </>
                    ) : (
                        <>
                            {indivCards.length ? (
                                <div className="sd-indiv">
                                    {indivCards.map(({ k, s, defs, series: cardSeries }) => (
                                        <div key={k} className="sd-card">
                                            <div className="sd-card__head">
                                                <span className="sd-card__title">{k === 'skeleton' && s === 'ok' ? `Skeleton · ${humanize(joint)}` : metricLabel(k)}</span>
                                                {k === 'skeleton' && s === 'ok' && joints.length ? (
                                                    <SelectField small ariaLabel="Joint" value={joint} onChange={setJoint} options={joints.map((j) => ({ value: j, label: humanize(j) }))} className="sd-card__joint" />
                                                ) : null}
                                            </div>
                                            {s === 'failed' ? (
                                                <div className="sd-card__state">
                                                    <span className="sd-card__fail">Didn’t load</span>
                                                    <Button onClick={retryFailed} disabled={retrying}>{retrying ? 'Retrying…' : 'Retry'}</Button>
                                                </div>
                                            ) : s === 'loading' ? (
                                                <Skel h={240} raised />
                                            ) : s !== 'ok' ? (
                                                <div className="sd-card__state"><span className="eg-small eg-secondary">Not recorded</span></div>
                                            ) : (
                                                <MetricChart
                                                    series={cardSeries}
                                                    duration={duration}
                                                    height={240}
                                                    brush={false}
                                                    markers={markers}
                                                    rightUnit={defs.some((d) => d.axis === 'right') ? defs.filter((d) => d.axis === 'right').map((d) => d.label).join(' · ') : ''}
                                                    playhead={t}
                                                    onSeek={seek}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {chips.parameter || chips.sessionevent ? (
                                <div className="sd-tables">
                                    {chips.parameter ? (
                                        <div className="sd-table-block">
                                            <span className="eg-caps-15">Parameters</span>
                                            {st('parameter') === 'failed' ? <span className="eg-small" style={{ color: 'var(--egdl-danger-ink)' }}>Parameters didn’t load.</span> : (
                                                <DataTable
                                                    dense
                                                    caption="Parameters"
                                                    emptyText="No game parameters recorded"
                                                    getKey={(r) => r.id}
                                                    columns={[
                                                        ...(multiPost && !isMobile ? [{ key: 'at', label: 'Time', w: '.7fr' }] : []),
                                                        { key: 'n', label: 'Parameter', w: '1.6fr', strong: true },
                                                        { key: 'v', label: 'Value', align: 'right' },
                                                        { key: 'ty', label: 'Type', muted: true },
                                                        ...(multiPost && isMobile ? [{ key: 'at', label: 'Time' }] : []),
                                                    ]}
                                                    rows={paramRows}
                                                />
                                            )}
                                        </div>
                                    ) : null}
                                    {chips.sessionevent ? (
                                        <div className="sd-table-block">
                                            <span className="eg-caps-15">Events</span>
                                            {st('sessionevent') === 'failed' ? <span className="eg-small" style={{ color: 'var(--egdl-danger-ink)' }}>Events didn’t load.</span> : (
                                                <>
                                                    {typed ? <EventTypeChips types={eventTypes} hidden={hiddenTypes} onToggle={toggleType} theme={theme} /> : null}
                                                    <EventTimeline
                                                        events={shownEvents}
                                                        now={t}
                                                        onSeek={seek}
                                                        emptyText={events.length ? 'All event types hidden' : 'No events recorded'}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {!indivCards.length && !chips.parameter && !chips.sessionevent ? (
                                <div className="eg-empty"><span className="eg-empty__body">No metric turned on</span></div>
                            ) : null}
                        </>
                    )}
                </div>

                {seriesSide ? series('side') : null}
            </div>
        </div>
    );
}
